(() => {
  const rootSelector = '[data-request-a-quote-summary]';
  const itemsSelector = '[data-request-a-quote-summary-items]';
  const selectedOptionsItemsSelector = '[data-request-a-quote-selected-options-items]';
  const productTitleSelector = '[data-request-a-quote-product-title]';
  const editSelector = '[data-quote-product-edit]';
  const legacyEditSelector = '[data-quote-product-edit-legacy]';
  const productImageSelector = '[data-request-a-quote-product-image]';
  const productPriceSelector = '[data-request-a-quote-product-price]';
  const productTaxCopySelector = '[data-request-a-quote-product-tax-copy]';
  const totalRowSelector = '[data-request-a-quote-summary-total-row]';
  const totalSelector = '[data-request-a-quote-summary-total]';
  const summaryReadySelector = '[data-request-a-quote-ready]';
  const selectionHeadingSelector = '[data-request-a-quote-selection-heading]';
  const editorSelector = '[data-quote-product-editor]';
  const editorMountSelector = '[data-quote-product-editor-mount]';
  const editorCloseSelector = '[data-quote-product-editor-close]';
  const quoteQuantityTemplateSelector = '[data-quote-quantity-template]';
  const cartQuoteSectionSelector = '[data-cart-quote-section]';
  const cartEmptyMessageSelector = '[data-cart-empty-message]';
  const quoteEditorOpenEvent = 'quote-editor:open';
  const quoteEditorSaveEvent = 'quote-editor:save';
  const quoteFormSubmittedEvent = 'quote-form:submitted';

  document.documentElement.requestAQuoteSummaryController?.abort?.();
  document.documentElement.dataset.requestAQuoteSummaryBound = 'true';

  const eventController = typeof AbortController === 'function' ? new AbortController() : null;
  const listenerOptions = eventController ? { signal: eventController.signal } : undefined;
  document.documentElement.requestAQuoteSummaryController = eventController;

  let activeEditSession = null;

  function getQuoteDraft() {
    return window.Express?.quoteDraft || null;
  }

  function formatCents(cents, currency) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: currency || 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format((Number(cents) || 0) / 100);
  }

  function formatPrice(row, currency) {
    if (row.priceLabel) return row.priceLabel;
    if (row.included) return 'Included';

    return formatCents(row.priceCents, currency);
  }

  function getPrimaryQuoteItem(quoteDraft) {
    if (!quoteDraft || typeof quoteDraft.getDraft !== 'function') return null;
    return quoteDraft.getDraft().items?.[0] || null;
  }

  function getQuoteSource(item) {
    return String(item?.properties?._quote_source || '').trim();
  }

  function queryRenderTarget(root, selector) {
    return root?.querySelector(selector) || document.querySelector(selector);
  }

  function isQuoteSubmitted(root) {
    return document.documentElement.dataset.quoteSubmitted === 'true' || root?.hasAttribute('data-quote-submitted');
  }

  function createSkeletonBlock(className) {
    return window.Express?.loadingSkeleton?.createBlock?.({
      className,
      variant: 'block',
      ariaHidden: true,
    });
  }

  function createSubmittedSkeletonLine() {
    const card = document.createElement('article');
    card.className = 'request-a-quote-summary__line request-a-quote-summary__line--submitted';
    card.setAttribute('role', 'listitem');
    card.setAttribute('aria-label', 'Quote request is being submitted');

    const media = window.Express?.loadingSkeleton?.createBlock?.({
      tagName: 'div',
      className: 'request-a-quote-summary__line-media request-a-quote-summary__skeleton-media',
      variant: '',
      ariaHidden: true,
    });

    const header = document.createElement('div');
    header.className = 'request-a-quote-summary__line-header';

    const heading = document.createElement('div');
    heading.className = 'request-a-quote-summary__line-heading';
    heading.append(
      createSkeletonBlock('request-a-quote-summary__skeleton-title'),
      createSkeletonBlock('request-a-quote-summary__skeleton-copy')
    );

    const total = document.createElement('div');
    total.className = 'request-a-quote-summary__line-unit-total request-a-quote-summary__skeleton-total';
    total.append(createSkeletonBlock('request-a-quote-summary__skeleton-price'));

    const controls = document.createElement('div');
    controls.className = 'request-a-quote-summary__line-controls';
    controls.append(createSkeletonBlock('request-a-quote-summary__skeleton-control'));

    const details = document.createElement('div');
    details.className = 'request-a-quote-summary__line-details';
    const options = document.createElement('div');
    options.className = 'request-a-quote-summary__line-options';
    options.append(
      createSkeletonBlock('request-a-quote-summary__skeleton-option'),
      createSkeletonBlock('request-a-quote-summary__skeleton-option request-a-quote-summary__skeleton-option--short')
    );
    details.appendChild(options);

    header.append(heading, total, controls);
    card.append(media, header, details);
    return card;
  }

  function renderSubmittedState(root) {
    const itemsRoot = queryRenderTarget(root, itemsSelector);
    const selectedOptionsItemsRoot = queryRenderTarget(root, selectedOptionsItemsSelector);
    const summaryReady = queryRenderTarget(root, summaryReadySelector);

    if (!itemsRoot) return false;

    itemsRoot.setAttribute('aria-busy', 'true');
    itemsRoot.replaceChildren(createSubmittedSkeletonLine());
    selectedOptionsItemsRoot?.replaceChildren();
    if (selectedOptionsItemsRoot) selectedOptionsItemsRoot.hidden = true;
    if (summaryReady) summaryReady.hidden = false;
    if (!syncCartQuoteState(root, true)) root.hidden = false;
    return true;
  }

  function createBrushIcon() {
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    icon.setAttribute('width', '24');
    icon.setAttribute('height', '24');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '2');
    icon.setAttribute('stroke-linecap', 'round');
    icon.setAttribute('stroke-linejoin', 'round');
    icon.setAttribute('class', 'lucide lucide-brush-icon lucide-brush');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    icon.innerHTML = '<path d="m11 10 3 3"/><path d="M6.5 21A3.5 3.5 0 1 0 3 17.5a2.62 2.62 0 0 1-.708 1.792A1 1 0 0 0 3 21z"/><path d="M9.969 17.031 21.378 5.624a1 1 0 0 0-3.002-3.002L6.967 14.031"/>';
    return icon;
  }

  function syncCartQuoteState(root, hasRows) {
    const cartQuoteSection = root.closest(cartQuoteSectionSelector);
    if (!cartQuoteSection) return false;

    cartQuoteSection.hidden = !hasRows;
    root.hidden = !hasRows;

    document.querySelectorAll(cartEmptyMessageSelector).forEach((message) => {
      if (message.dataset.cartEmpty === 'true') message.hidden = hasRows;
    });

    return true;
  }

  function getProductUrl(item) {
    const productUrl = String(item?.url || item?.product_url || '').trim();
    if (productUrl) return productUrl;

    const handle = String(item?.handle || item?.product_handle || '').trim().replace(/^\/+|\/+$/g, '');
    return handle ? `/products/${handle}` : '';
  }

  function getBuilderSnapshot(item) {
    if (getQuoteSource(item) === 'custom_variant_picker') return null;

    const snapshot = item?.builder || null;
    if (!snapshot || snapshot.type !== 'product_builder' || !snapshot.config) return null;
    return snapshot;
  }

  function getQuoteItemTitle(item) {
    return getBuilderSnapshot(item)
      ? item?.product_title || item?.title || 'Selected product'
      : item?.title || item?.product_title || 'Selected product';
  }

  function parseJsonObject(value) {
    if (!value) return null;
    if (typeof value === 'object') return value;

    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  function normalizeProductImage(image, fallbackAlt) {
    if (!image) return null;

    if (typeof image === 'string') {
      return image ? { url: image, alt: fallbackAlt || '' } : null;
    }

    const url = image.url || image.src || image.thumbnailUrl || '';
    if (!url) return null;

    return {
      url,
      alt: image.alt || image.altText || fallbackAlt || '',
    };
  }

  function normalizeThumbnailImage(image, fallbackAlt) {
    if (!image) return null;

    if (typeof image === 'string') {
      return image ? { url: image, alt: fallbackAlt || '' } : null;
    }

    const url = image.thumbnailUrl || image.url || image.src || image.previewUrl || '';
    if (!url) return null;

    return {
      url,
      alt: image.alt || image.altText || fallbackAlt || '',
    };
  }

  function getPriceCents(row) {
    return Number(row?.priceCents ?? row?.priceDeltaCents ?? row?.price_delta_cents ?? row?.price ?? 0) || 0;
  }

  function isIncluded(row) {
    return Boolean(row?.included || getPriceCents(row) === 0);
  }

  function splitOptionTitle(title) {
    const text = String(title || '');
    const separatorIndex = text.indexOf(':');

    if (separatorIndex === -1) {
      return { groupLabel: '', optionLabel: text.trim() };
    }

    return {
      groupLabel: text.slice(0, separatorIndex).trim(),
      optionLabel: text.slice(separatorIndex + 1).trim(),
    };
  }

  function normalizeFallbackOption(input = {}) {
    const titleParts = splitOptionTitle(input.title || input.label || '');
    const groupLabel = input.groupLabel || input.type || titleParts.groupLabel;
    const optionLabel = input.optionLabel || input.value || titleParts.optionLabel || input.title || '';
    const label = groupLabel ? `${groupLabel}: ${optionLabel}` : optionLabel;

    return {
      label,
      title: optionLabel || label,
      type: groupLabel,
      groupLabel,
      optionLabel,
      image: normalizeThumbnailImage(input.image, optionLabel || label),
      priceCents: getPriceCents(input),
      included: isIncluded(input),
      priceLabel: input.priceLabel ? String(input.priceLabel) : '',
    };
  }

  function normalizeOptionMatchKey(value) {
    return String(value || '').trim().toLowerCase();
  }

  function getResolvedQuoteOptionRows(item) {
    const metadata = parseJsonObject(item?.properties?._quote_options) || {};
    return (Array.isArray(metadata.items) ? metadata.items : []).map(normalizeFallbackOption).filter((row) => row.label);
  }

  function findResolvedQuoteOptionRow(rows, option) {
    const groupLabel = normalizeOptionMatchKey(option.groupLabel);
    const optionLabel = normalizeOptionMatchKey(option.label);

    return rows.find((row) => (
      normalizeOptionMatchKey(row.groupLabel) === groupLabel
      && normalizeOptionMatchKey(row.optionLabel) === optionLabel
    )) || null;
  }

  function getSelectedBuilderOptions(snapshot) {
    const config = snapshot?.config || {};
    const selections = snapshot?.selections || {};

    return (config.groups || []).flatMap((group) => {
      const rawSelection = Array.isArray(selections[group.groupTypeKey])
        ? selections[group.groupTypeKey]
        : (selections[group.groupTypeKey] ? [selections[group.groupTypeKey]] : []);

      return rawSelection.map((optionKey) => {
        const option = (group.options || []).find((entry) => String(entry.key) === String(optionKey));
        if (!option) return null;

        return {
          ...option,
          groupKey: group.groupTypeKey,
          groupLabel: group.label || group.groupTypeKey || '',
        };
      }).filter(Boolean);
    });
  }

  function getOptionImage(option) {
    const previewMedia = Array.isArray(option?.previewMedia) ? option.previewMedia : [];
    const candidates = [
      previewMedia[0],
      option?.preview,
      option?.previewImage,
      option?.thumbnail,
      option?.image,
    ];

    for (const candidate of candidates) {
      const image = normalizeProductImage(candidate, option?.label || option?.optionLabel || 'Selected option');
      if (image) return image;
    }

    return null;
  }

  function getOptionThumbnail(option) {
    const previewMedia = Array.isArray(option?.previewMedia) ? option.previewMedia : [];
    const candidates = [
      option?.thumbnail,
      option?.image,
      previewMedia[0],
      option?.preview,
      option?.previewImage,
    ];

    for (const candidate of candidates) {
      const image = normalizeThumbnailImage(candidate, option?.label || option?.optionLabel || 'Selected option');
      if (image) return image;
    }

    return null;
  }

  function buildBuilderSummaryRows(item) {
    const snapshot = getBuilderSnapshot(item);
    if (!snapshot) return [];

    const config = snapshot.config || {};
    const productTitle = config.product?.title || config.title || item?.product_title || item?.title || 'Base price';
    const basePriceCents = Number(config.product?.basePriceCents ?? item?.price ?? item?.final_price ?? 0) || 0;
    const baseImage = normalizeThumbnailImage(config.product?.product_image || config.product?.images?.[0] || item?.image, productTitle);
    const selectedOptions = getSelectedBuilderOptions(snapshot);
    const resolvedOptionRows = getResolvedQuoteOptionRows(item);

    return [
      {
        label: productTitle,
        title: productTitle,
        type: 'Base price',
        image: baseImage,
        priceCents: basePriceCents,
        included: false,
        priceLabel: '',
      },
      ...selectedOptions.map((option) => {
        const resolvedRow = findResolvedQuoteOptionRow(resolvedOptionRows, option);
        const priceCents = resolvedRow ? getPriceCents(resolvedRow) : getPriceCents(option);

        return {
          label: `${option.groupLabel}: ${option.label}`,
          title: option.label || '',
          type: option.groupLabel,
          groupKey: option.groupKey,
          optionKey: option.key,
          groupLabel: option.groupLabel,
          optionLabel: option.label || '',
          image: getOptionThumbnail(option),
          priceCents,
          included: resolvedRow ? isIncluded(resolvedRow) : isIncluded(option),
          priceLabel: resolvedRow?.priceLabel || '',
        };
      }),
    ];
  }

  function buildBuilderSelectedOptionRows(item) {
    return getSelectedBuilderOptions(getBuilderSnapshot(item)).map((option) => ({
      label: `${option.groupLabel}: ${option.label}`,
      groupLabel: option.groupLabel,
      optionLabel: option.label || '',
      image: getOptionImage(option),
      priceCents: getPriceCents(option),
      included: isIncluded(option),
      priceLabel: '',
    }));
  }

  function buildFallbackSummaryRows(quoteDraft, item) {
    if (!item || typeof quoteDraft?.buildQuoteOptions !== 'function') return [];

    return quoteDraft.buildQuoteOptions(item).map((row) => ({
      ...normalizeFallbackOption(row),
      label: row.title || row.label || '',
    }));
  }

  function buildFallbackSelectedOptionRows(item) {
    const metadata = parseJsonObject(item?.properties?._quote_options) || {};
    return (Array.isArray(metadata.items) ? metadata.items : []).map(normalizeFallbackOption).filter((row) => row.label);
  }

  function buildSummaryRows(quoteDraft, item) {
    const builderRows = buildBuilderSummaryRows(item);
    if (builderRows.length) return builderRows;

    return buildFallbackSummaryRows(quoteDraft, item);
  }

  function buildSelectedOptionRows(item) {
    const builderRows = buildBuilderSelectedOptionRows(item);
    if (builderRows.length) return builderRows;

    return buildFallbackSelectedOptionRows(item);
  }

  function findGstIncludedCopy(item) {
    const metadata = parseJsonObject(item?.properties?._quote_options) || {};
    const snapshot = getBuilderSnapshot(item);
    const candidates = [
      metadata.gstIncludedCopy,
      metadata.taxCopy,
      metadata.summaryNote,
      snapshot?.config?.ui?.summaryNote,
      snapshot?.config?.notes,
    ];

    return candidates.find((copy) => /gst/i.test(String(copy || '')) && /include/i.test(String(copy || ''))) || '';
  }

  function updateProductSummary(root, quoteItem, totalLabel, currency) {
    const productTitle = queryRenderTarget(root, productTitleSelector);
    const productImage = queryRenderTarget(root, productImageSelector);
    const productPrice = queryRenderTarget(root, productPriceSelector);
    const productTaxCopy = queryRenderTarget(root, productTaxCopySelector);
    const title = getQuoteItemTitle(quoteItem);
    const image = normalizeProductImage(quoteItem?.image, title);
    const gstCopy = findGstIncludedCopy(quoteItem);

    if (productTitle) productTitle.textContent = title;

    if (productImage) {
      if (image?.url) {
        productImage.src = image.url;
        productImage.alt = image.alt || title;
        productImage.hidden = false;
      } else {
        productImage.removeAttribute('src');
        productImage.alt = '';
        productImage.hidden = true;
      }
    }

    if (productPrice) {
      productPrice.textContent = totalLabel || formatCents(0, currency);
    }

    if (productTaxCopy) {
      productTaxCopy.textContent = gstCopy;
      productTaxCopy.hidden = !gstCopy;
    }
  }

  function getItemLineKey(quoteDraft, item, index) {
    if (typeof quoteDraft?.getLineKey === 'function') return quoteDraft.getLineKey(item);
    return item?.line_key || String(index);
  }

  function getLineTotalCents(item) {
    return Number(item?.final_line_price ?? item?.line_price ?? 0) || Math.round((Number(item?.final_price ?? item?.price ?? 0) || 0) * (Number(item?.quantity) || 1));
  }

  function getDraftTotalCents(quoteDraft, quoteItems = []) {
    if (typeof quoteDraft?.getTotal === 'function') {
      return Math.round(quoteDraft.getTotal(quoteItems) * 100);
    }

    return quoteItems.reduce((sum, item) => sum + getLineTotalCents(item), 0);
  }

  function isBasePriceRow(row) {
    return /base price/i.test(String(row?.type || row?.title || row?.label || ''));
  }

  function createSelectedOptionMedia(row) {
    const media = document.createElement('div');
    media.className = 'request-a-quote-selected-options__media';

    if (row.image?.url) {
      const image = document.createElement('img');
      image.src = row.image.url;
      image.alt = row.image.alt || row.optionLabel || row.label || '';
      image.loading = 'lazy';
      media.appendChild(image);
      return media;
    }

    const placeholder = document.createElement('span');
    placeholder.className = 'request-a-quote-selected-options__media-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    media.appendChild(placeholder);
    return media;
  }

  function createSummaryMedia(row, tagName = 'div') {
    const media = document.createElement(tagName);
    media.className = 'request-a-quote-summary__media';

    if (row.image?.url) {
      const image = document.createElement('img');
      image.src = row.image.url;
      image.alt = row.image.alt || row.title || row.label || '';
      image.loading = 'lazy';
      media.appendChild(image);
      return media;
    }

    const placeholder = document.createElement('span');
    placeholder.className = 'request-a-quote-summary__media-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    media.appendChild(placeholder);
    return media;
  }

  function createSummaryRow(row, currency) {
    const isEditorTrigger = Boolean(row.groupKey) && !isBasePriceRow(row);
    const item = document.createElement('div');
    item.setAttribute('role', 'listitem');

    const content = document.createElement(isEditorTrigger ? 'button' : 'div');
    content.className = 'request-a-quote-summary__item';

    if (isEditorTrigger) {
      content.type = 'button';
      content.dataset.quoteProductEdit = '';
      content.dataset.quoteBuilderGroupKey = String(row.groupKey);
      if (row.optionKey) content.dataset.quoteBuilderOptionKey = String(row.optionKey);
      content.setAttribute('aria-haspopup', 'dialog');
      content.setAttribute('aria-label', `Edit ${row.type || row.groupLabel || 'option'}: ${row.title || row.optionLabel || row.label || ''}`);
    }

    const labelWrap = document.createElement(isEditorTrigger ? 'span' : 'dl');
    labelWrap.className = 'request-a-quote-summary__label-wrap';

    const title = document.createElement(isEditorTrigger ? 'span' : 'dt');
    title.className = 'request-a-quote-summary__title';
    title.textContent = row.title || row.optionLabel || row.label || '';
    labelWrap.appendChild(title);

    if (row.type || row.groupLabel) {
      const type = document.createElement(isEditorTrigger ? 'span' : 'dd');
      type.className = 'request-a-quote-summary__type text-xs-u';
      type.textContent = row.type || row.groupLabel;
      labelWrap.appendChild(type);
    }

    const price = document.createElement(isEditorTrigger ? 'span' : 'p');
    price.className = 'request-a-quote-summary__price';
    price.textContent = formatPrice(row, currency);

    content.append(createSummaryMedia(row, isEditorTrigger ? 'span' : 'div'), labelWrap, price);
    item.appendChild(content);
    return item;
  }

  function createIncludedRowsDisclosure(rows, currency) {
    const disclosure = document.createElement('details');
    disclosure.className = 'request-a-quote-summary__included';

    const summary = document.createElement('summary');
    summary.className = 'request-a-quote-summary__included-summary';
    summary.textContent = `What's included (${rows.length})`;

    const list = document.createElement('div');
    list.className = 'request-a-quote-summary__included-list';
    list.setAttribute('role', 'list');
    rows.forEach((row) => list.appendChild(createSummaryRow(row, currency)));

    disclosure.append(summary, list);
    return disclosure;
  }

  function appendOptionRows(container, optionRows, currency) {
    const includedRows = optionRows.filter((row) => isIncluded(row));
    const pricedRows = optionRows.filter((row) => !isIncluded(row));

    pricedRows.forEach((row) => container.appendChild(createSummaryRow(row, currency)));

    if (includedRows.length > 1) {
      container.appendChild(createIncludedRowsDisclosure(includedRows, currency));
      return;
    }

    includedRows.forEach((row) => container.appendChild(createSummaryRow(row, currency)));
  }

  function createQuoteQuantityInput(index, title, quantity) {
    const template = document.querySelector(quoteQuantityTemplateSelector);
    const quantityId = `QuoteLineQuantity-${index}`;
    const quantityField = template?.content?.firstElementChild?.cloneNode(true);

    if (!quantityField) return document.createTextNode('');

    const input = quantityField.querySelector('[data-quote-quantity-input]');
    const context = quantityField.querySelector('[data-quote-quantity-context]');
    const value = quantityField.querySelector('[data-quote-quantity-value]');
    const decrease = quantityField.querySelector('[data-quote-quantity-decrease]');
    const increase = quantityField.querySelector('[data-quote-quantity-increase]');
    const remove = quantityField.querySelector('[data-quote-remove]');

    if (input) {
      input.id = quantityId;
      input.value = String(quantity);
    }
    if (context) context.textContent = `Quantity for ${title}:`;
    if (value) value.textContent = String(quantity);
    if (decrease) decrease.disabled = quantity <= 1;
    if (increase) increase.disabled = quantity >= 100;

    [decrease, increase].forEach((button) => {
      if (!button) return;
      const hiddenLabel = button.querySelector('.visually-hidden');
      if (hiddenLabel) {
        hiddenLabel.textContent = `${button === decrease ? 'Decrease' : 'Increase'} quantity for ${title}`;
      }
    });

    if (remove) remove.setAttribute('aria-label', `Remove ${title}`);

    return quantityField;
  }

  function createQuoteLineCard(quoteDraft, item, index, currency) {
    const lineKey = getItemLineKey(quoteDraft, item, index);
    const title = getQuoteItemTitle(item);
    const image = normalizeProductImage(item?.image, title);
    const quantity = Number(item?.quantity) || 1;
    const summaryRows = buildSummaryRows(quoteDraft, item);
    const baseRow = summaryRows.find(isBasePriceRow);
    const optionRows = summaryRows.filter((row) => row !== baseRow);
    const hasBuilder = Boolean(getBuilderSnapshot(item));

    const card = document.createElement('article');
    card.className = 'request-a-quote-summary__line';
    card.setAttribute('role', 'listitem');
    card.dataset.quoteLineKey = lineKey;
    card.dataset.quoteLineIndex = String(index);

    const media = document.createElement('div');
    media.className = 'request-a-quote-summary__line-media';
    if (image?.url) {
      const imageElement = document.createElement('img');
      imageElement.src = image.url;
      imageElement.alt = image.alt || title;
      imageElement.loading = 'lazy';
      media.appendChild(imageElement);
    } else {
      const placeholder = document.createElement('span');
      placeholder.setAttribute('aria-hidden', 'true');
      media.appendChild(placeholder);
    }

    const header = document.createElement('div');
    header.className = 'request-a-quote-summary__line-header';

    const headingWrap = document.createElement('div');
    headingWrap.className = 'request-a-quote-summary__line-heading';
    const productUrl = getProductUrl(item);
    const heading = document.createElement(productUrl ? 'a' : 'span');
    heading.className = 'request-a-quote-summary__line-title';
    if (productUrl) {
      heading.href = productUrl;
    } else {
      heading.setAttribute('role', 'heading');
      heading.setAttribute('aria-level', '4');
    }
    heading.textContent = title;
    headingWrap.appendChild(heading);

    const unitPrice = document.createElement('p');
    unitPrice.className = 'request-a-quote-summary__line-unit-price';
    const unitPriceValue = document.createElement('strong');
    unitPriceValue.textContent = baseRow ? formatPrice(baseRow, currency) : formatCents(Number(item?.price ?? item?.final_price ?? 0) || 0, currency);
    const unitPriceSuffix = document.createElement('span');
    unitPriceSuffix.textContent = ' / unit';
    unitPrice.append(unitPriceValue, unitPriceSuffix);
    headingWrap.appendChild(unitPrice);

    const lineTotalSummary = document.createElement('p');
    lineTotalSummary.className = 'request-a-quote-summary__line-unit-total';
    const lineTotalSummaryLabel = document.createElement('span');
    lineTotalSummaryLabel.textContent = 'Line total';
    const lineTotalSummaryValue = document.createElement('strong');
    lineTotalSummaryValue.textContent = formatCents(getLineTotalCents(item), currency);
    lineTotalSummary.append(lineTotalSummaryValue, lineTotalSummaryLabel);

    const controls = document.createElement('div');
    controls.className = 'request-a-quote-summary__line-controls';
    const quantityLabel = document.createElement('span');
    quantityLabel.className = 'request-a-quote-summary__quantity-label sr-only';
    quantityLabel.textContent = 'Qty';
    const quantityField = createQuoteQuantityInput(index, title, quantity);
    controls.append(quantityLabel, quantityField);

    if (hasBuilder) {
      const edit = document.createElement('button');
      edit.type = 'button';
      edit.className = 'button button--rainbow button--xsmall ep-product-builder-modal__button request-a-quote-selected-options__edit';
      edit.dataset.quoteProductEdit = '';
      edit.setAttribute('aria-haspopup', 'dialog');
      edit.setAttribute('aria-label', `Edit options for ${title}`);
      const editLabel = document.createElement('span');
      editLabel.textContent = 'Edit';
      edit.append(createBrushIcon(), editLabel);
      controls.appendChild(edit);
    }

    header.append(headingWrap, lineTotalSummary, controls);

    const details = document.createElement('div');
    details.className = 'request-a-quote-summary__line-details';

    if (optionRows.length) {
      const optionsList = document.createElement('div');
      optionsList.className = 'request-a-quote-summary__line-options';
      optionsList.setAttribute('role', 'list');
      appendOptionRows(optionsList, optionRows, currency);
      details.appendChild(optionsList);
    }

    card.append(media, header, details);
    return card;
  }

  function createSelectedOptionCard(row) {
    const item = document.createElement('div');
    item.className = 'request-a-quote-selected-options__item';
    item.setAttribute('role', 'listitem');

    const content = document.createElement('div');
    content.className = 'request-a-quote-selected-options__content';

    const title = document.createElement('h4');
    title.className = 'request-a-quote-selected-options__title';
    title.textContent = row.optionLabel || row.label || '';

    const type = document.createElement('p');
    type.className = 'request-a-quote-selected-options__type';
    type.textContent = row.groupLabel || '';

    content.append(title, type);
    item.append(createSelectedOptionMedia(row), content);
    return item;
  }

  function renderList(root) {
    if (isQuoteSubmitted(root) && renderSubmittedState(root)) return;

    const quoteDraft = getQuoteDraft();
    const itemsRoot = queryRenderTarget(root, itemsSelector);
    const selectedOptionsItemsRoot = queryRenderTarget(root, selectedOptionsItemsSelector);
    const legacyEditButton = document.querySelector(legacyEditSelector);
    const totalRow = queryRenderTarget(root, totalRowSelector);
    const total = queryRenderTarget(root, totalSelector);
    const summaryReady = queryRenderTarget(root, summaryReadySelector);
    const selectionHeading = queryRenderTarget(root, selectionHeadingSelector);

    if (!quoteDraft || !itemsRoot || typeof quoteDraft.getDraft !== 'function') {
      syncCartQuoteState(root, false);
      root.hidden = true;
      return;
    }

    const draft = quoteDraft.getDraft();
    const quoteItems = draft.items || [];
    const quoteItem = quoteItems[0] || null;
    const selectedOptionRows = quoteItems.flatMap(buildSelectedOptionRows);
    const hasRows = quoteItems.length > 0;
    const selectedQuantity = quoteItems.reduce((sum, item) => sum + (Number(item?.quantity) || 0), 0);

    itemsRoot.removeAttribute('aria-busy');
    itemsRoot.replaceChildren(...quoteItems.map((item, index) => createQuoteLineCard(quoteDraft, item, index, draft.currency)));
    selectedOptionsItemsRoot?.replaceChildren(...selectedOptionRows.map(createSelectedOptionCard));
    if (selectedOptionsItemsRoot) selectedOptionsItemsRoot.hidden = !selectedOptionRows.length;
    if (summaryReady) summaryReady.hidden = !hasRows;
    if (selectionHeading) selectionHeading.textContent = `Your selection (${selectedQuantity})`;
    if (!syncCartQuoteState(root, hasRows)) root.hidden = false;

    if (legacyEditButton) {
      legacyEditButton.hidden = true;
      legacyEditButton.disabled = true;
    }

    if (totalRow && total) {
      const totalCents = getDraftTotalCents(quoteDraft, quoteItems);

      const totalLabel = formatCents(totalCents, draft.currency);
      total.textContent = totalLabel;
      totalRow.hidden = !hasRows;
      updateProductSummary(root, quoteItem, totalLabel, draft.currency);
    } else {
      updateProductSummary(root, quoteItem, '', draft.currency);
    }
  }

  function renderAll() {
    document.querySelectorAll(rootSelector).forEach(renderList);
  }

  function createBuilder(snapshot, quoteItem = null) {
    const builder = document.createElement('express-product-builder');
    builder.className = 'ep-product-builder';
    builder.dataset.builderMode = 'quote-edit';
    builder.dataset.productBuilderRoot = '';
    const configData = {
      ...(snapshot.config || {}),
      product: {
        ...(snapshot.config?.product || {}),
      },
    };
    const parentSku = getQuoteLineSku(quoteItem || {}) || getQuoteLineSku({ builder: { config: configData } });
    if (parentSku) configData.product.sku = parentSku;

    const mount = document.createElement('div');
    mount.dataset.productBuilderApp = '';

    const config = document.createElement('script');
    config.type = 'application/json';
    config.dataset.productBuilderConfig = '';
    config.textContent = JSON.stringify(configData);

    builder.append(mount, config);
    return builder;
  }

  function getQuoteItemForIdentity(quoteDraft, identity = {}) {
    const items = quoteDraft?.getDraft?.().items || [];
    const key = identity.key;
    const index = Number(identity.index);

    if (key && typeof quoteDraft?.getLineKey === 'function') {
      const match = items.find((item) => quoteDraft.getLineKey(item) === key || item.line_key === key);
      if (match) return match;
    }

    return Number.isInteger(index) ? items[index] : items[0];
  }

  function getQuoteItemForTrigger(quoteDraft, trigger) {
    return getQuoteItemForIdentity(quoteDraft, getQuoteLineIdentity(trigger));
  }

  function getQuoteLineIdentity(trigger) {
    const line = trigger?.closest?.('[data-quote-line-key]');

    return {
      key: trigger?.dataset?.quoteLineKey || line?.dataset?.quoteLineKey || '',
      index: trigger?.dataset?.quoteLineIndex || line?.dataset?.quoteLineIndex || '',
    };
  }

  function normalizeQuoteEditorRequest(request = {}) {
    const trigger = request.sourceTrigger || null;
    const triggerIdentity = getQuoteLineIdentity(trigger);
    const requestIdentity = request.identity || {};

    return {
      ...request,
      sourceTrigger: trigger,
      identity: {
        key: requestIdentity.key || request.lineKey || triggerIdentity.key || '',
        index: requestIdentity.index || request.lineIndex || triggerIdentity.index || '',
      },
    };
  }

  function getQuoteLineSku(item = {}) {
    const properties = item.properties || {};

    return String(
      item.sku ||
      item.variant_sku ||
      properties._parent_sku ||
      properties.parent_sku ||
      properties._sku ||
      properties.product_sku ||
      item.builder?.config?.product?.sku ||
      getQuoteLineSkuFromKey(item.line_key || item.key) ||
      ''
    ).trim();
  }

  function getQuoteLineSkuFromKey(lineKey) {
    const key = String(lineKey || '');
    const match = key.match(/"(?:_parent_sku|parent_sku|_sku|product_sku)"\s*:\s*"([^"\\]*(?:\\.[^"\\]*)*)"/);
    if (!match) return '';

    try {
      return JSON.parse(`"${match[1]}"`);
    } catch (_error) {
      return match[1];
    }
  }

  function showQuoteEditorSaveError(builder, error) {
    const message = error instanceof Error
      ? error.message
      : 'Unable to save this product configuration. Please refresh and try again.';

    console.warn('[Express quote editor] Unable to save quote line.', error);

    if (builder?.state) {
      builder.state.errorMessage = message;
      if (typeof builder.resetSubmittingState === 'function') {
        builder.resetSubmittingState();
      } else if (typeof builder.render === 'function') {
        builder.state.isSubmitting = false;
        builder.state.submittingAction = '';
        builder.render();
      }
    }
  }

  function mergeQuoteLineUpdate(existingItem, lineItem) {
    if (!existingItem) return lineItem;
    const sku = getQuoteLineSku(lineItem) || getQuoteLineSku(existingItem);

    return {
      ...existingItem,
      ...lineItem,
      id: lineItem.id || existingItem.id,
      variant_id: lineItem.variant_id || lineItem.id || existingItem.variant_id || existingItem.id,
      product_id: lineItem.product_id || existingItem.product_id,
      handle: lineItem.handle || existingItem.handle,
      url: lineItem.url || existingItem.url,
      image: lineItem.image || existingItem.image,
      quantity: existingItem.quantity,
      sku,
    };
  }

  function dispatchQuoteEditorOpen(trigger) {
    const identity = getQuoteLineIdentity(trigger);

    trigger.dispatchEvent(new CustomEvent(quoteEditorOpenEvent, {
      bubbles: true,
      detail: {
        identity,
        lineKey: identity.key,
        lineIndex: identity.index,
        targetGroupKey: trigger?.dataset?.quoteBuilderGroupKey || '',
        targetOptionKey: trigger?.dataset?.quoteBuilderOptionKey || '',
        sourceTrigger: trigger,
      },
    }));
  }

  function openQuoteEditor(request = {}) {
    request = normalizeQuoteEditorRequest(request);
    const quoteDraft = getQuoteDraft();
    const trigger = request.sourceTrigger || null;
    const identity = request.identity;
    const quoteItem = getQuoteItemForIdentity(quoteDraft, identity) || getQuoteItemForTrigger(quoteDraft, trigger) || getPrimaryQuoteItem(quoteDraft);
    const snapshot = getBuilderSnapshot(quoteItem);
    const dialog = document.querySelector(editorSelector);
    const mount = dialog?.querySelector(editorMountSelector);

    if (!dialog || !mount || !snapshot) return;

    activeEditSession = {
      trigger: trigger || null,
      identity,
      item: quoteItem,
    };
    mount.replaceChildren(createBuilder(snapshot, quoteItem));

    const builder = mount.querySelector('express-product-builder');
    const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
    schedule(() => {
      if (typeof builder?.setSelections === 'function') {
        builder.setSelections(snapshot.selections || {});
      }
      if (request.targetGroupKey && typeof builder?.navigateToGroup === 'function') {
        builder.navigateToGroup(request.targetGroupKey);
      }
    });

    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  }

  function saveQuoteEditorLine(lineItem, eventTarget) {
    const quoteDraft = getQuoteDraft();
    if (!activeEditSession || !quoteDraft || typeof quoteDraft.replaceItem !== 'function' || !lineItem) return;

    const lineIdentity = activeEditSession.identity || {};
    const lineIndex = Number(lineIdentity.index);
    const identity = lineIdentity.key || (Number.isInteger(lineIndex) ? lineIndex : 0);
    const existingItem = getQuoteItemForIdentity(quoteDraft, lineIdentity) || activeEditSession.item;
    try {
      quoteDraft.replaceItem(identity, mergeQuoteLineUpdate(existingItem, lineItem));
      closeEditor(eventTarget?.closest?.(editorSelector) || document.querySelector(editorSelector));
    } catch (error) {
      showQuoteEditorSaveError(eventTarget, error);
    }
  }

  function closeEditor(dialog) {
    if (!dialog) return;

    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      dialog.dispatchEvent(new Event('close'));
    }
  }

  document.addEventListener('click', (event) => {
    const editButton = event.target.closest(editSelector);
    if (editButton) {
      dispatchQuoteEditorOpen(editButton);
      return;
    }

    const quantityButton = event.target.closest('[data-quote-quantity-decrease], [data-quote-quantity-increase]');
    if (quantityButton) {
      if (quantityButton.disabled) return;

      const root = quantityButton.closest(rootSelector);
      const quantityField = quantityButton.closest('[data-quote-quantity]');
      const quantityInput = quantityField?.querySelector('[data-quote-quantity-input]');
      const quantityValue = quantityField?.querySelector('[data-quote-quantity-value]');
      if (!quantityInput) return;

      const isIncrease = quantityButton.hasAttribute('data-quote-quantity-increase');
      const delta = isIncrease ? 1 : -1;
      const quantity = Math.max(1, Math.min(100, (Number(quantityInput.value) || 1) + delta));
      quantityInput.value = String(quantity);
      if (quantityValue) quantityValue.textContent = String(quantity);
      const decrease = quantityField.querySelector('[data-quote-quantity-decrease]');
      const increase = quantityField.querySelector('[data-quote-quantity-increase]');
      if (decrease) decrease.disabled = quantity <= 1;
      if (increase) increase.disabled = quantity >= 100;

      const quoteDraft = getQuoteDraft();
      const lineIdentity = getQuoteLineIdentity(quantityButton);
      const identity = lineIdentity.key || lineIdentity.index;
      if (quoteDraft && typeof quoteDraft.updateItemQuantity === 'function') {
        quoteDraft.updateItemQuantity(identity, quantity);
        const updatedLine = Array.from(root?.querySelectorAll('[data-quote-line-key]') || []).find((line) => (
          line.dataset.quoteLineKey === lineIdentity.key || line.dataset.quoteLineIndex === lineIdentity.index
        ));
        const focusSelector = isIncrease
          ? (quantity >= 100 ? '[data-quote-quantity-decrease]' : '[data-quote-quantity-increase]')
          : (quantity <= 1 ? '[data-quote-quantity-increase]' : '[data-quote-quantity-decrease]');
        updatedLine?.querySelector(focusSelector)?.focus();
      }
      return;
    }

    const removeButton = event.target.closest('[data-quote-remove]');
    if (removeButton) {
      const quoteDraft = getQuoteDraft();
      const lineIdentity = getQuoteLineIdentity(removeButton);
      const identity = lineIdentity.key || lineIdentity.index;
      if (quoteDraft && typeof quoteDraft.removeItem === 'function') quoteDraft.removeItem(identity);
      return;
    }

    const closeButton = event.target.closest(editorCloseSelector);
    if (closeButton) {
      closeEditor(closeButton.closest(editorSelector));
    }
  }, listenerOptions);

  document.addEventListener('change', (event) => {
    const quantityInput = event.target.closest('[data-quote-quantity-input]');
    if (!quantityInput) return;

    const quoteDraft = getQuoteDraft();
    const lineIdentity = getQuoteLineIdentity(quantityInput);
    const identity = lineIdentity.key || lineIdentity.index;
    if (quoteDraft && typeof quoteDraft.updateItemQuantity === 'function') {
      quoteDraft.updateItemQuantity(identity, quantityInput.value);
    }
  }, listenerOptions);

  document.addEventListener('product-builder:quote-edit-cancel', (event) => {
    closeEditor(event.target.closest(editorSelector) || document.querySelector(editorSelector));
  }, listenerOptions);

  document.addEventListener('product-builder:quote-update', (event) => {
    document.dispatchEvent(new CustomEvent(quoteEditorSaveEvent, {
      detail: {
        lineItem: event.detail?.lineItem || null,
        sourceBuilder: event.target,
      },
    }));
  }, listenerOptions);

  document.addEventListener(quoteEditorOpenEvent, (event) => {
    openQuoteEditor(event.detail || {});
  }, listenerOptions);

  document.addEventListener(quoteEditorSaveEvent, (event) => {
    saveQuoteEditorLine(event.detail?.lineItem, event.detail?.sourceBuilder || event.target);
  }, listenerOptions);

  const editor = document.querySelector(editorSelector);
  if (editor) {
    editor.addEventListener('close', () => {
      const mount = editor.querySelector(editorMountSelector);
      mount?.replaceChildren();
      if (activeEditSession?.trigger?.isConnected && typeof activeEditSession.trigger.focus === 'function') {
        activeEditSession.trigger.focus();
      }
      activeEditSession = null;
    }, listenerOptions);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderAll, listenerOptions);
  } else {
    renderAll();
  }

  document.addEventListener('quote-draft:updated', renderAll, listenerOptions);
  document.addEventListener(quoteFormSubmittedEvent, renderAll, listenerOptions);
})();
